import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { TOWN_PAGE_SIZE, townPageHref } from "@/lib/construction/places";

type TownPaginationProps = {
  region: string;
  townSlug: string;
  townName: string;
  total: number;
  /** Current page number, or 0 on the town page itself. */
  current?: number;
};

/** Pages to link: first, last, two either side of the current one, and every tenth, so every page is a few clicks away. */
function pageNumbers(count: number, current: number): number[] {
  const pages = new Set<number>([1, count]);
  for (let p = current - 2; p <= current + 2; p++) if (p >= 1 && p <= count) pages.add(p);
  if (current === 0) for (let p = 2; p <= Math.min(count, 5); p++) pages.add(p);
  for (let p = 10; p < count; p += 10) pages.add(p);
  return Array.from(pages).sort((a, b) => a - b);
}

/**
 * Plain links to numbered pages of every company in a town. Server-rendered, so
 * search engines can reach every company page, unlike the "Show more" button.
 */
export function TownPagination({ region, townSlug, townName, total, current = 0 }: TownPaginationProps) {
  const count = Math.ceil(total / TOWN_PAGE_SIZE);
  if (count < 1 || (current === 0 && total <= 30)) return null;
  const numbers = pageNumbers(count, current);
  const linkClass =
    "inline-flex h-10 min-w-10 items-center justify-center rounded-full border border-ink/15 bg-white px-3 text-sm font-semibold text-ink transition-colors hover:border-ink/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink";

  return (
    <nav aria-label={`Pages of construction companies in ${townName}`} className="mt-10">
      {current === 0 ? (
        <p className="mb-4 text-center text-sm text-ink-muted">Browse every company in {townName}, {TOWN_PAGE_SIZE} per page:</p>
      ) : null}
      <ul className="flex flex-wrap items-center justify-center gap-2">
        {current > 1 ? (
          <li>
            <Link href={townPageHref(region, townSlug, current - 1)} rel="prev" className={linkClass}>
              <ChevronLeft className="size-4" aria-hidden="true" />
              <span className="sr-only">Previous page</span>
            </Link>
          </li>
        ) : null}
        {numbers.map((p, i) => (
          <li key={p} className="flex items-center gap-2">
            {i > 0 && p - numbers[i - 1] > 1 ? (
              <span aria-hidden="true" className="text-ink-muted">
                …
              </span>
            ) : null}
            {p === current ? (
              <span aria-current="page" className={cn(linkClass, "border-ink bg-ink text-white hover:border-ink")}>
                {p}
              </span>
            ) : (
              <Link href={townPageHref(region, townSlug, p)} className={linkClass}>
                <span className="sr-only">Page </span>
                {p}
              </Link>
            )}
          </li>
        ))}
        {current >= 1 && current < count ? (
          <li>
            <Link href={townPageHref(region, townSlug, current + 1)} rel="next" className={linkClass}>
              <ChevronRight className="size-4" aria-hidden="true" />
              <span className="sr-only">Next page</span>
            </Link>
          </li>
        ) : null}
      </ul>
    </nav>
  );
}
